const attribute = (name) => new RegExp(`${name}\\s*=\\s*["']+\\s*([^"'>]+?)\\s*["']+`, "i");

const normaliseHref = (href) => href.replace(/([^:])\/{2,}/g, "$1/");

// Editors paste raw <link rel="alternate" /> markup into these fields, and the CMS
// export sometimes mangles the quoting into repeated double quotes, so match loosely.
const parseMarkup = (value = "") =>
  value
    .split(/<link/i)
    .slice(1)
    .map((tag) => {
      const hrefLang = tag.match(attribute("hreflang"))?.[1];
      const href = tag.match(attribute("href"))?.[1];

      return hrefLang && href ? { hrefLang, href: normaliseHref(href) } : null;
    })
    .filter((i) => !!i);

// SourceFlowHead keys each tag by hrefLang, so duplicates would collide.
const dedupe = (entries) => {
  const seen = new Set();

  return entries.filter(({ hrefLang }) => {
    if (seen.has(hrefLang)) return false;
    seen.add(hrefLang);
    return true;
  });
};

// Matches how SourceFlowHead resolves a page in metadata.json: asPath, query dropped,
// trailing slash enforced.
const toMetaPath = (asPath = "") => {
  const pathname = asPath.split("?")[0];

  return pathname.endsWith("/") ? pathname : `${pathname}/`;
};

// Site wide metadata, keyed by URL — the source for pages that are not built in the
// page builder. SourceFlowHead only reads a fixed set of ids, so `alternates` is ours.
export const getMetaLanguageAlternates = (metaObject, asPath) => {
  const entry = (metaObject?.[toMetaPath(asPath)] ?? []).find((i) => i.id === "alternates");

  return dedupe(parseMarkup(entry?.attributes?.hreflang));
};

export const getLanguageAlternates = ({ hreflang_alternates, alternate } = {}) => {
  const entries = [];

  // SEO tab "Hreflang Alternates" repeater — structured, but self-referencing only.
  Object.values(hreflang_alternates ?? {}).forEach((i) => {
    if (i?.hreflang && i?.href) {
      entries.push({ hrefLang: i.hreflang, href: normaliseHref(i.href) });
    }
  });

  // SEO tab "Alternate" pair — editors paste the full set of tags into the hreflang
  // field as raw markup, which is the only way to point at another domain.
  const legacy = alternate?.hreflang ?? "";
  if (/<link/i.test(legacy)) {
    entries.push(...parseMarkup(legacy));
  } else if (legacy.trim() && alternate?.url) {
    entries.push({ hrefLang: legacy.trim(), href: normaliseHref(alternate.url) });
  }

  return dedupe(entries);
};

export default getLanguageAlternates;
